'use client';

/**
 * Image component that produces a low quality placeholder image using Fastly's
 * query param API. This image is shown while the main image loads in. When the
 * main image has finished loading, the placeholder fades out.
 *
 * Because the placeholder cannot know the main image's intrinsic size, that
 * information must be provided via props, specifically the `placeholderImage`
 * prop.
 *
 * The placeholder purposely ignores click events so users can interact with
 * the main image (open in a new tab, download, copy, etc.).
 */

import { createComponent } from '@carvana/merch-components';
import cx from 'classnames';
import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import optimizeImage, {
  type Options as OptimizeImageOptions
} from '~/utilities/optimizeImage';

export type ImageProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  containerClassName?: string;

  height?: number;

  onLoad?: (event: Event) => void;

  optimize?: OptimizeImageOptions;

  /**
   * Attributes to pass to placeholder image.
   */
  placeholderImage?: React.ImgHTMLAttributes<HTMLImageElement> & {
    containerClassName?: string;
  } & (
      | {
          /**
           * Amount to blur the placeholder image.
           */
          blurAmount?: number;

          height?: number;

          url?: never;

          /**
           * (Optional) Height to pass to Fastly. Defaults to main image height.
           * Value will be divided by two.
           */
          urlHeight?: number;

          /**
           * (Optional) Width to pass to Fastly. Defaults to main image width.
           * Value will be divided by two.
           */
          urlWidth?: number;

          width?: number;
        }
      | {
          blurAmount?: never;

          height?: never;

          /**
           * Placeholder image URL.
           */
          url: string;

          urlHeight?: never;

          urlWidth?: never;

          width?: never;
        }
    );

  /**
   * Enable progressive loading effect where image fades in gradually
   */
  progressiveLoading?: boolean;

  /**
   * Whether main image should load or not. Useful when lazy loading images.
   */
  shouldLoad?: boolean;

  src: string;

  width?: number;

  wrapperClassName?: string;
};

const ImageContainer = createComponent('div', {
  className: 'col-span-full row-span-full flex overflow-hidden'
});

function generatePlaceholderUrl(
  imageUrl: string,
  blurAmount: number,
  height?: number,
  width?: number
): string {
  return imageUrl.toLowerCase().includes('fastly')
    ? optimizeImage(imageUrl, {
        blur: blurAmount,
        height: height && height / 2,
        optimize: 'high',
        width: width && width / 2
      })
    : '';
}

const dummyFn = () => {};

export default memo<ImageProps>(
  forwardRef<HTMLImageElement, ImageProps>(function Image(
    {
      alt,
      className: imageClassName,
      containerClassName: imageContainerClassName,
      height,
      width,
      loading,
      onLoad = dummyFn,
      optimize,
      placeholderImage = {},
      progressiveLoading = false,
      shouldLoad = true,
      src = '',
      wrapperClassName,
      ...props
    }: ImageProps,
    ref
  ) {
    const {
      blurAmount = 25,
      className: placeholderClassName = imageClassName,
      containerClassName:
        placeholderContainerClassName = imageContainerClassName,
      height: placeholderHeight = height,
      width: placeholderWidth = width,
      loading: placeholderLoading = loading,
      url: placeholderUrl,
      urlHeight = placeholderHeight,
      urlWidth = placeholderWidth,
      ...restPlaceholderImage
    } = placeholderImage;

    const imageRef = useRef<HTMLImageElement>(null);

    // Expose image ref to parent component
    useImperativeHandle<HTMLImageElement | null, HTMLImageElement | null>(
      ref,
      () => imageRef.current
    );

    src = optimize ? optimizeImage(src, optimize) : src;

    const placeholderUrlToUse = useMemo(
      () =>
        placeholderUrl ??
        generatePlaceholderUrl(src, blurAmount, urlHeight, urlWidth),
      [blurAmount, placeholderUrl, src, urlHeight, urlWidth]
    );

    const [isLoaded, setLoaded] = useState(false);

    const showImage = shouldLoad || isLoaded;
    const showPlaceholderImage = Boolean(placeholderUrlToUse);

    const onLoadRef = useRef(onLoad);

    useEffect(() => {
      onLoadRef.current = onLoad;
    });

    // Because image may have finished loading before the `onLoad` event listener is attached,
    // we need to check if the image is already loaded and set the state accordingly.
    useEffect(() => {
      const el = imageRef.current;

      if (!el) return;

      function onLoadHandler(ev: Event) {
        setLoaded(true);
        onLoadRef.current(ev);
      }

      el.addEventListener('load', onLoadHandler);

      if (el.complete) {
        if (el.naturalWidth > 0) {
          el.dispatchEvent(new Event('load'));
        } else {
          setLoaded(true);
        }
      }

      return () => {
        el.removeEventListener('load', onLoadHandler);
      };
    }, [showImage, src]);

    return src ? (
      <div
        className={cx('grid max-h-full max-w-full u:w-fit', wrapperClassName)}
      >
        {showImage && (
          <ImageContainer
            className={cx(
              imageContainerClassName,
              progressiveLoading
                ? 'z-10'
                : cx(
                    'transition-all duration-500',
                    isLoaded ? 'opacity-100' : 'opacity-0'
                  )
            )}
          >
            <img
              {...props}
              ref={imageRef}
              src={src}
              alt={alt}
              className={imageClassName}
              height={height}
              width={width}
              loading={loading}
            />
          </ImageContainer>
        )}
        {showPlaceholderImage && (
          <ImageContainer
            className={cx(
              'pointer-events-none',
              placeholderContainerClassName,
              progressiveLoading && 'z-0',
              !progressiveLoading && (isLoaded ? 'opacity-0' : 'opacity-100'),
              !progressiveLoading && 'transition-opacity duration-500'
            )}
          >
            <img
              {...props}
              {...restPlaceholderImage}
              src={placeholderUrlToUse}
              alt={alt}
              className={placeholderClassName}
              height={height}
              width={width}
              loading={placeholderLoading}
            />
          </ImageContainer>
        )}
      </div>
    ) : null;
  })
);
